/**
 * Desktop-parity chart colours — the SINGLE SOURCE OF TRUTH for anything drawn
 * on a SciChart surface or an overlay above one.
 *
 * Every value comes from viscom's manifest, extracted from the viscom_liner
 * SOURCE (skin resources + the converters that resolve them), not sampled from a
 * screenshot. Screenshot-sampled colour is unreliable twice over: the desktop
 * composites alpha over its own background, and image capture re-encodes.
 *
 * The SCSS side of the same manifest lives in `src/_theme.scss`. Keep the two in
 * step — a value that appears in both is listed here with a pointer.
 *
 * Rule: no component hardcodes a chart colour. If you need one that is not here,
 * add it here first.
 */

/** Line series strokes. */
export const SERIES_COLORS = {
  /** Current temperature — /temperature and /curing. */
  currentTemperature: '#FFFFFF',
  /**
   * Maximal temperature — the running per-position max.
   *
   * Manifest: solid #FF0000, thickness 2, NO dash. Curing had #F71C1C, which is
   * a near-miss rather than the desktop value, so it moves here too — the two
   * views draw the same quantity and must not disagree about its colour.
   */
  maximalTemperature: '#FF0000',
} as const;

/** Uniform stroke weight for the temperature traces (manifest: thickness 2). */
export const SERIES_THICKNESS = 2;

/** Series names, which are also the legend labels. */
export const SERIES_NAMES = {
  currentTemperature: 'Current temperature',
  maximalTemperature: 'Maximal temperature',
} as const;

/**
 * Time-over bands on /curing, ASCENDING BY THRESHOLD.
 *
 * Index 0 is the coolest stage and index 3 the hottest, matching the order
 * heatInputStages() returns and the order the desktop legend prints:
 * purple / green / red / blue, then the curing band in its own green.
 *
 * ORDER IS LOAD-BEARING, not decorative. A cooler threshold is met for longer, so
 * its mountain encloses every hotter one; the series are added coolest-first so
 * the shorter bands land on top of the taller ones rather than behind them.
 */
export const HEAT_INPUT_BAND_COLORS = [
  '#871EDF', // stage 1 — coolest
  '#9EDC37', // stage 2
  '#F56442', // stage 3
  '#328BE3', // stage 4 — hottest
] as const;

/** The curing-temperature band, and the what-if line and badge that track it. */
export const CURING_BAND_COLOR = '#30B453';

/**
 * Band fill alpha on screen.
 *
 * These were 0.9 — all but opaque. With one band drawn that reads as a tinted
 * region; with FIVE it reads as one flat slab, because each band paints over the
 * taller one it sits inside and the nesting that is the entire point of the
 * display disappears. The desktop tints and hatches so all five stay legible
 * through each other. 0.32 is the value at which the deepest overlap — five bands
 * stacked — is still lighter than a single opaque fill.
 */
export const BAND_ALPHA = 0.32;

/**
 * Diagonal hatch over each band, drawn on a 2D overlay above the WebGL surface.
 *
 * SciChart's mountain fill takes a colour, not a brush: there is no pattern or
 * texture option on FastMountainRenderableSeries, so the hatch cannot come from
 * the series itself. It is painted by CuringBandHatchComponent, which clips a
 * repeating diagonal to each band's silhouette.
 *
 * Same 45-degree rising rule as OFFLINE_HATCH so the two read as one language,
 * but finer and fainter: the offline hatch is a warning covering the whole plot,
 * these are five nested regions that must stay readable through one another.
 */
export const BAND_HATCH = {
  /**
   * Pen width in CSS pixels.
   *
   * MEASURED, not guessed. At 1.5px on a 9px tile at 0.55 alpha the hatch covered
   * 1.3% of the plot — present in a pixel count and invisible to a person at 1:1,
   * which is the worst of both: it passes a "does it paint" test and does nothing
   * for the viewer. The desktop's rules read plainly at normal zoom. 2px on a 7px
   * tile is ~29% coverage, the same order as OFFLINE_HATCH's 3-on-12.
   */
  penThicknessPx: 2,
  /** Tile size in CSS pixels; pen-to-tile ratio sets how dense the rule reads. */
  tilePx: 7,
  /** Applied to the band's own colour, so each hatch matches its series. */
  opacity: 0.75,
} as const;

/**
 * Status palette. Mirrors --vc-status-* / --vc-severity-* in _theme.scss.
 */
export const STATUS_COLORS = {
  info: '#2B9EFD',
  success: '#53CB47',
  warning: '#FFB922',
  error: '#FF501D',
} as const;

/**
 * Fault severity, indexed by FiberFault.FT.
 *
 * TRAP, called out in the manifest: FiberBreak is GREY, not red. It reads as the
 * most severe state and the instinct is to paint it the most alarming colour;
 * the desktop deliberately does not. Note this is the DOT colour — the left nav
 * treats FiberBreak as an error state and does tint red (see --vc-nav-error),
 * so the same condition is legitimately two colours in two places.
 */
export const SEVERITY_COLORS = {
  ok: '#53CB47',
  warning: '#FFB922',
  error: '#FF501D',
  fiberBreak: '#4B4B4B',
} as const;

/**
 * Zone state colour map, ZR codes 0-4.
 *
 * Codes 2 and 3 are the SAME green in the desktop — not an oversight to
 * "correct". The gradient stops are doubled up at each boundary to make the map
 * stepped rather than blended: a zone is in exactly one state, so an
 * interpolated colour between two states would be a state that does not exist.
 */
export const ZONE_COLORS = {
  0: '#0000FF', // Blue
  1: '#FFA500', // Orange
  2: '#32CD32', // LimeGreen
  3: '#32CD32', // LimeGreen — same as 2, per the desktop
  4: '#FF0000', // Red
} as const;

/** Stepped gradient stops for a HeatmapColorMap over ZR 0..4. */
export const ZONE_GRADIENT_STOPS = [
  { offset: 0.0, color: ZONE_COLORS[0] },
  { offset: 0.2, color: ZONE_COLORS[0] },
  { offset: 0.2, color: ZONE_COLORS[1] },
  { offset: 0.4, color: ZONE_COLORS[1] },
  { offset: 0.4, color: ZONE_COLORS[2] },
  { offset: 0.8, color: ZONE_COLORS[3] },
  { offset: 0.8, color: ZONE_COLORS[4] },
  { offset: 1.0, color: ZONE_COLORS[4] },
];

/**
 * Annotation / overlay colours.
 *
 * SciChart takes #RRGGBBAA, so the fills carry their alpha inline. The desktop's
 * ARGB (#AARRGGBB) values from the manifest are re-ordered here, NOT recomputed:
 * #B4FF0000 (ARGB, alpha 180) becomes #FF0000B4 (RGBA).
 */
export const OVERLAY_COLORS = {
  /** Cure band — the in-spec temperature window. */
  cureBand: '#32CD32',
  cureBandFill: '#32CD3255',

  /** Above the overheat threshold (OT). */
  overheatBand: '#FF0000',
  overheatBandFill: '#FF000044',

  /** Curing threshold line. */
  curingThreshold: '#53CB47',
  curingThresholdThickness: 2,

  /** Fault-region shading, by severity (desktop ARGB #B4FF0000 / #B4FF9933). */
  faultRegionError: '#FF0000B4',
  faultRegionWarning: '#FF9933B4',

  /**
   * Fault marker line at the fault's position. Solid, unlike the region fill —
   * the region says "roughly here", the line says "exactly here".
   */
  faultMarkerLine: '#FF0000',

  /** Liner schematic / liner-bound markers. */
  linerStroke: '#00FF00',
  linerStrokeThickness: 2,

  /** View icons overlaid on a surface (desktop ARGB #CCFFFFFF). */
  viewIcon: '#FFFFFFCC',
} as const;

/**
 * Gridlines. The manifest gives opacity only, so the hue is the chrome
 * foreground and the opacity rides in the alpha byte: 0.25 -> 40, 0.10 -> 1A.
 */
export const GRIDLINE_OPACITY = {
  major: 0.25,
  minor: 0.1,
} as const;

export const GRIDLINE_STYLE = {
  major: { color: '#FFFFFF40', strokeThickness: 1 },
  minor: { color: '#FFFFFF1A', strokeThickness: 1 },
} as const;

/**
 * Device-offline hatch.
 *
 * 45-degree RISING lines, 3px pen on a 12px tile at half opacity. The previous
 * approximation used a 10px line on a 20px tile at 0.16 alpha, which reads as a
 * wash rather than the desktop's distinct diagonal rules.
 */
export const OFFLINE_HATCH = {
  color: '#D60000',
  /** Applied to the pen, giving rgba(214, 0, 0, 0.5). */
  opacity: 0.5,
  penThicknessPx: 3,
  tilePx: 12,
} as const;

/**
 * Hover cursor (CursorModifier) on /temperature.
 *
 * MANIFEST-PENDING: viscom is describing the desktop's modifier and label
 * placement. Until then these keep the green the temperature chart's rollover
 * line already used, so the cursor reads as part of this chart rather than
 * arriving in an unrelated colour.
 */
export const CURSOR_COLORS = {
  line: '#41B972',
  lineThickness: 2,
  /** Background of the x-axis readout at the foot of the line. */
  labelFill: '#41B972',
  labelText: '#FFFFFF',
} as const;

/**
 * PRINT PALETTE — the report is a white page, the app is a dark console.
 *
 * applyTheme() restyles the CHROME (background, gridlines, tick labels) but not a
 * series' explicit stroke, and this is where that matters: the current-temperature
 * trace is #FFFFFF. On a dark console it is the most legible line on the chart;
 * on a white report page it is INVISIBLE. Simply applying a light theme therefore
 * produced a report whose main trace had silently vanished.
 *
 * So the capture also swaps strokes, keyed by the app colour, and puts them back
 * afterwards. Only colours that fail on white are listed — anything absent is
 * left exactly as the browser draws it, which keeps the two in step by default
 * rather than by maintenance.
 */
export const PRINT_STROKE_OVERRIDES: ReadonlyArray<[string, string]> = [
  // Current temperature: white -> near-black. The desktop report draws it dark.
  ['#FFFFFF', '#1A1A1A'],
  ['#ffffff', '#1A1A1A'],
  // Liner bounds: pure green is thin and glary on white.
  ['#00FF00', '#0A8F0A'],
  // Neutral schematic/ink that was white for a dark background.
  ['#FFFFFFCC', '#1A1A1ACC'],
];

/**
 * Ground for the report surfaces — TINTED, not pure white.
 *
 * Rob, holding the old desktop report beside the new one: "there's a nice
 * background here too". Joel: "that kind of helps the colors not get washed out."
 *
 * WHAT IT DOES AND DOES NOT DO, measured rather than assumed. The obvious
 * rationale — "a translucent fill over white washes out, over grey it keeps its
 * hue" — is WRONG, and this comment said it until the numbers were run. Contrast
 * of each band fill against its own ground:
 *
 *              purple  lime  salmon  blue  green
 *   over white   1.53   1.14   1.32   1.33   1.27
 *   over tint    1.51   1.11   1.30   1.31   1.25
 *
 * The tint makes fill contrast very slightly WORSE, because it darkens the fill
 * and the ground by the same proportion. It is worth having for a different
 * reason: it gives the plot a defined edge on the page, so the chart reads as a
 * panel rather than as marks floating on paper, and it stops the white page
 * glare that made everything on it look faint. The washed-out FILLS are fixed by
 * REPORT_BAND_COLORS and REPORT_BAND_ALPHA below, which is where that problem
 * actually lives.
 *
 * Faint on purpose. This is a printed document: a strong tint would cost toner
 * on every chart page and make the report look like a screenshot of a console.
 * #F1F3F6 is about 4% down from white.
 */
export const REPORT_PLOT_BACKGROUND = '#F1F3F6';

/** Chrome for the report surfaces: light page, legible dark furniture. */
export const PRINT_THEME_OVERRIDES = {
  sciChartBackground: REPORT_PLOT_BACKGROUND,
  loadingAnimationBackground: REPORT_PLOT_BACKGROUND,
  majorGridLineBrush: '#00000038',
  minorGridLineBrush: '#00000018',
  tickTextBrush: '#1A1A1A',
  axisTitleColor: '#1A1A1A',
  axisBandsFill: '#00000000',
  rolloverLineBrush: '#1A1A1A',
} as const;

/**
 * Gridlines for the report, and why the theme above is not enough on its own.
 *
 * `majorGridLineBrush` in a theme only reaches an axis that did not set its own
 * style. Every axis in this app DOES: `majorGridLineStyle: GRIDLINE_STYLE.major`
 * appears on fifteen axes across curing, temperature, graph and health, and
 * GRIDLINE_STYLE.major is '#FFFFFF40' — white, for a dark console.
 *
 * So on the report page the gridlines were WHITE ON WHITE and did not exist.
 * Not faint: absent. Look at any chart page of a report generated before this
 * change and there is not one gridline on it, which is a large part of why the
 * charts read as floating shapes rather than as measurements.
 *
 * The capture swaps these in per axis and puts the originals back, the same way
 * it already does for series strokes.
 */
export const PRINT_GRIDLINE_STYLE = {
  major: { color: '#00000038', strokeThickness: 1 },
  minor: { color: '#00000018', strokeThickness: 1 },
} as const;

/**
 * WHERE THE IMAGE ENDS UP, which is what every print size below is derived from.
 *
 * ReportApiServices places each chart with `.Image(bytes).FitWidth()` on an A4
 * page with a 1.5 cm margin. The PNG is therefore scaled to a 180 mm column
 * WHATEVER pixel size it arrives at — 180 mm = 510.24 pt. Capturing at a higher
 * resolution buys sharpness and nothing else: it does not make one thing on the
 * chart a single point bigger on paper. Only the RATIO of a feature to the image
 * width decides its printed size.
 *
 * This is the fact the axis labels were built without.
 */
export const REPORT_CONTENT_WIDTH_PT = 510.24;

/**
 * Axis text size ON PAPER, in points — Rob: "when you get into this, this font's
 * really small", "if you zoom out of this, you can't read it."
 *
 * MEASURED BEFORE CHANGING ANYTHING. On a shipping 4800 px curing capture the
 * y-axis digits have a 20 px cap height. Against the 180 mm column that is
 * 9.4 px per point, so a 2.1 pt cap — roughly a 3 POINT FONT, beside 10 pt body
 * text on the same page. Rob was being polite.
 *
 * THE CAUSE IS NOT THE DOWNSCALE, and that distinction decides the fix. The
 * export blows the chart up to 2400 CSS px while SciChart keeps its ~13 px screen
 * default, so the label shrinks to about a third of the proportion it has on
 * screen. Had it been the downscale, capturing larger would have helped; it does
 * not, and that is worth knowing before anyone reaches for a bigger canvas.
 *
 * Which is also why these are POINTS and not pixels. A pixel size would mean a
 * different printed size for every capture geometry — and heatmap3D is now
 * captured at a different geometry from the rest. The target is what the reader
 * sees; the pixel size is derived from it.
 */
export const REPORT_AXIS_LABEL_PT = 9;
export const REPORT_AXIS_TITLE_PT = 10;

/**
 * Trace weight on paper, in points.
 *
 * Same arithmetic, same oversight: SERIES_THICKNESS is 2 CSS px, which on a
 * 2400 px capture prints as a 0.43 pt hairline. Thin red on white is exactly the
 * "washed out" Joel described, and it is a stroke problem rather than a colour
 * problem — the red is already #FF0000 and cannot get any more saturated.
 */
export const REPORT_SERIES_STROKE_PT = 1;

/**
 * Outline weight for a FILLED series (the time-over bands) on paper.
 *
 * Not the relative rule the traces get. A band's outline is its identity: the
 * five nest inside one another, every overlap is a new blended colour, and two
 * of the five are greens that a fill alone cannot separate. On screen the hatch
 * does this job by carrying the series colour at full strength; the hatch is a
 * fourth canvas and is not in the capture, so on paper the outline is all there
 * is. At the relative rule a 1 px band edge printed at 0.5 pt — present, and not
 * doing the job.
 */
export const REPORT_BAND_STROKE_PT = 0.85;

/** CSS pixels that print as `pt` points, for a capture `cssWidth` CSS px wide. */
export function reportFontPx(pt: number, cssWidth: number): number {
  return Math.max(1, Math.round((pt * cssWidth) / REPORT_CONTENT_WIDTH_PT));
}

/**
 * A stroke of `px` screen pixels, re-weighted for the report.
 *
 * Anchored so the main trace (SERIES_THICKNESS) lands on
 * REPORT_SERIES_STROKE_PT, and everything else keeps its weight RELATIVE to it —
 * a 1 px guide stays half a trace, a 4 px emphasis stays double. Never returns
 * less than the original, so this can only ever thicken.
 */
export function reportStrokePx(px: number, cssWidth: number): number {
  const perPt = cssWidth / REPORT_CONTENT_WIDTH_PT;
  const scaled = Math.round(
    (px * REPORT_SERIES_STROKE_PT * perPt) / SERIES_THICKNESS
  );
  return Math.max(px, scaled);
}

/**
 * The WATERFALL colour scale — temperature, not zone state.
 *
 * NOT to be confused with ZONE_GRADIENT_STOPS above, which maps the ZR state
 * CODES 0..4 in stepped bands. This one is a continuous ramp over a TEMPERATURE
 * domain, and the two happen to share their four colours because the desktop
 * picked the state colours from this ramp. Merging them would be wrong in a way
 * nothing would catch: the offsets mean different things.
 *
 * The offsets are the desktop's, from GraphViewModel.cs ZoneGradient — heating
 * at 0.194 and curing at 0.389 of a -10..180 C domain, i.e. 27 C and 64 C.
 *
 * Read by the heatmap series, by the legend beside it, and by the colour scale
 * the report draws on the 3D Graph page. Three hand-maintained copies of a ramp
 * is how a report ends up explaining the chart next to it incorrectly.
 */
export const WATERFALL_TEMP_GRADIENT_STOPS: ReadonlyArray<{
  offset: number;
  color: string;
}> = [
  { offset: 0, color: '#0000FF' },
  { offset: 0.194444444444445, color: '#FFA500' },
  { offset: 0.388888888888889, color: '#32CD32' },
  { offset: 1, color: '#FF0000' },
];

/** The waterfall scale's domain in CELSIUS; callers convert for display. */
export const WATERFALL_TEMP_DOMAIN_C = { min: -10, max: 180 } as const;

/**
 * REPORT MODE — chosen at CONSTRUCTION, not patched afterwards.
 *
 * 887 tried to recolour a built chart by writing series.stroke at capture time.
 * That failed twice over: SciChart normalises colour strings, so a lookup keyed
 * on '#FFFFFF' misses a stroke it reports back in another form; and the legend
 * builds its swatches when the modifier is created, so a later stroke change
 * leaves the swatch showing the original colour. The report came out with a white
 * line and a white legend chip on a white page.
 *
 * Building in report mode instead means the series, the legend and the bands all
 * agree, because they are all constructed from the same values — the same
 * builder, themed, which is the actual requirement.
 */
let reportMode = false;

export function setReportMode(on: boolean): void {
  reportMode = on;
}
export function isReportMode(): boolean {
  return reportMode;
}

/** Current-temperature stroke: white on the console, near-black on paper. */
export function currentTemperatureStroke(): string {
  return reportMode ? '#1A1A1A' : SERIES_COLORS.currentTemperature;
}

/** Maximal-temperature stroke. Red reads on both, so it does not change. */
export function maximalTemperatureStroke(): string {
  return SERIES_COLORS.maximalTemperature;
}

/**
 * Band fill, thinned for paper.
 *
 * The time-over bands are drawn at 0.9 alpha — nearly opaque. Over a dark plot
 * that reads as a tinted region; over a WHITE plot it becomes a solid slab of
 * colour and the page stops looking white at all, which is what the salmon
 * 25 degree band did to the whole chart. The desktop tints; it does not cover.
 */
export function bandFill(rgba: string): string {
  if (!reportMode) {
    return rgba;
  }
  const m = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/.exec(rgba);
  if (!m) {
    return rgba;
  }
  return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${REPORT_BAND_ALPHA})`;
}

/**
 * Band fill alpha on paper.
 *
 * Was 0.25, chosen when the report ground was pure white and five nested bands
 * at anything stronger turned the page into a slab. Measured against the tinted
 * ground and the darker print colours below, 0.34 lifts every band's separation
 * from the ground (dE 17-31 -> 24-37) while the deepest five-deep overlap still
 * reads as a tint rather than a fill.
 */
export const REPORT_BAND_ALPHA = 0.34;

/**
 * Darker band colours FOR PAPER — Joel: "use higher contrast colors that stand
 * out over the white background better."
 *
 * The console palette is chosen against a near-black plot, where a light,
 * desaturated colour is the legible choice. On paper every one of them is a
 * pastel: at 0.25 alpha over white the lime band scores 1.14 against its ground,
 * which is a contrast ratio of essentially nothing. These keep the hue — so a
 * band is recognisably the same band between the app and the report — and take
 * the luminance down until the fill is a colour rather than a wash.
 *
 * WHAT THIS DOES NOT FIX, and it is worth saying because measuring it is the only
 * way anyone would know. Stage 2 (lime) and the curing band (green) sit dE 10-11
 * apart AS FILLS, before and after this change: two greens, and darkening both
 * moves them together. That ambiguity is in the desktop's own palette and is not
 * mine to repaint on a demo morning. What separates them on screen is the HATCH,
 * which carries each series colour at full strength; the printed equivalent is
 * the band OUTLINE, which is why filled series get their own heavier print
 * stroke weight in the capture rather than inheriting the trace rule.
 */
const REPORT_BAND_COLORS: Readonly<Record<string, string>> = {
  '#871edf': '#5B12A0', // stage 1 purple
  '#9edc37': '#4F7A0E', // stage 2 lime
  '#f56442': '#C1341A', // stage 3 salmon
  '#328be3': '#1257A0', // stage 4 blue
  '#30b453': '#1B7536', // curing green
};

/**
 * A band's colour for wherever it is being drawn — print variant in report mode,
 * the app's own otherwise.
 *
 * Applied at CONSTRUCTION, which is the only place it can be applied reliably:
 * ExportPrerenderService rebuilds EVERY report chart offscreen with report mode
 * on, precisely so the series, the fills and the legend swatches are all built
 * from one set of values instead of patched afterwards.
 */
export function bandInk(hex: string): string {
  if (!reportMode) {
    return hex;
  }
  return REPORT_BAND_COLORS[hex.trim().toLowerCase()] ?? hex;
}

/**
 * A band's screen fill from its series colour — the one place the two are tied
 * together, so the legend swatch, the mountain and the hatch cannot drift apart.
 */
export function bandFillFor(hex: string, alpha: number = BAND_ALPHA): string {
  const { r, g, b } = hexToRgb(bandInk(hex));
  return bandFill(`rgba(${r}, ${g}, ${b}, ${alpha})`);
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) {
    return { r: 255, g: 255, b: 255 };
  }
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Marker line colour, darkened for paper when it would not read on white.
 *
 * The marker palette was chosen against a near-black plot, so several entries —
 * #C6FA64, #4BF0C8, #40ECFA, #E7E04F — are pale by design. A 2.4px line in those
 * colours on a WHITE report page is somewhere between faint and invisible, which
 * is indistinguishable from the series not being drawn at all.
 *
 * Hue is preserved so a marker stays recognisable between the browser and the
 * report; only luminance is pulled down, and only for colours that actually fail.
 */
export function markerStroke(color: string): string {
  if (!reportMode) {
    return color;
  }
  const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) {
    return color;
  }
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  // Rec. 601 luma. Anything brighter than this disappears into the page.
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  if (luma <= PRINT_MAX_MARKER_LUMA) {
    return color;
  }
  const scale = PRINT_MAX_MARKER_LUMA / luma;
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v * scale)));
  const hex = (v: number) => v.toString(16).padStart(2, '0');
  return `#${hex(clamp(r))}${hex(clamp(g))}${hex(clamp(b))}`;
}

/** Luma ceiling for a marker line on white. Below mid-grey stays legible. */
export const PRINT_MAX_MARKER_LUMA = 140;
